import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import { WidgetCard, QuickActionButton } from "@/components/widgets";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
    PlusCircle,
    Package,
    Receipt,
    PackagePlus,
    ShoppingCart,
    Wallet,
    Users,
    Truck,
    BarChart3,
    Sparkles,
    TrendingUp,
    TrendingDown,
    ArrowRight,
    Coins,
    Zap,
    AlertTriangle,
    Trophy,
    Activity,
    Sun,
} from "lucide-react";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const yesterdayISO = () => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
};
const todayISO = () => new Date().toISOString().slice(0, 10);

// --------- Business Health Score (0-100, computed from KPIs) ---------
function computeHealthScore({ finance, exec }) {
    if (!finance || !exec) return { score: null, breakdown: [] };
    let score = 0;
    const breakdown = [];

    // Revenue signal (30)
    if (finance.cash_basis.revenue_today > 0) { score += 30; breakdown.push({ label: "Revenue flowing", delta: +30, good: true }); }
    else { breakdown.push({ label: "No revenue today yet", delta: 0, good: false }); }

    // Receivables health (20) — receivables under 30 days revenue
    const netCash30d = finance.cash_flow_30d.reduce((s, x) => s + x.net, 0);
    if (netCash30d >= 0) { score += 20; breakdown.push({ label: "Positive 30-day cash flow", delta: +20, good: true }); }
    else { breakdown.push({ label: "Negative 30-day cash flow", delta: 0, good: false }); }

    // Overdue penalty
    if (exec.attention.overdue_invoices === 0) { score += 15; breakdown.push({ label: "No overdue invoices", delta: +15, good: true }); }
    else { breakdown.push({ label: `${exec.attention.overdue_invoices} overdue invoice(s)`, delta: 0, good: false }); }

    // Inventory health
    if (exec.attention.out_of_stock_skus === 0) { score += 15; breakdown.push({ label: "Nothing out of stock", delta: +15, good: true }); }
    else { breakdown.push({ label: `${exec.attention.out_of_stock_skus} SKUs out of stock`, delta: 0, good: false }); }

    if (exec.attention.low_stock_skus <= 2) { score += 10; breakdown.push({ label: "Low-stock under control", delta: +10, good: true }); }
    else { breakdown.push({ label: `${exec.attention.low_stock_skus} SKUs running low`, delta: 0, good: false }); }

    // Growth signal
    const change = exec.performance.change_pct;
    if (change !== null && change >= 0) { score += 10; breakdown.push({ label: `${change >= 0 ? "+" : ""}${change}% vs prior period`, delta: +10, good: true }); }
    else if (change !== null) { breakdown.push({ label: `${change}% vs prior period`, delta: 0, good: false }); }
    else { score += 5; breakdown.push({ label: "First period — establishing baseline", delta: +5, good: true }); }

    return { score, breakdown };
}

// --------- Rule-based AI Recommendation of the Day ---------
function computeRecommendation({ finance, exec, inventory }) {
    if (!finance || !exec) return null;
    const cards = [];
    // Highest-priority first
    if (exec.attention.overdue_value > 0) {
        cards.push({
            severity: "high",
            title: "Follow up on overdue receivables",
            body: `${inr(exec.attention.overdue_value)} across ${exec.attention.overdue_invoices} invoice${exec.attention.overdue_invoices > 1 ? "s" : ""} is past due. Call the top overdue customer today.`,
            cta: { label: "See Business Intelligence", path: "/business-intelligence" },
        });
    }
    if (exec.attention.out_of_stock_skus > 0) {
        cards.push({
            severity: "high",
            title: `${exec.attention.out_of_stock_skus} SKU${exec.attention.out_of_stock_skus > 1 ? "s are" : " is"} out of stock`,
            body: "Lost-sale risk. Reorder immediately or invoice will fail on send.",
            cta: { label: "Open Inventory", path: "/inventory-intelligence" },
        });
    }
    if (exec.attention.low_stock_skus >= 3) {
        cards.push({
            severity: "med",
            title: `${exec.attention.low_stock_skus} SKUs running low`,
            body: "Cluster them into one purchase order to save on freight and paperwork.",
            cta: { label: "Create Purchase Order", path: "/procurement" },
        });
    }
    if (finance.outstanding.payables > finance.outstanding.receivables && finance.outstanding.payables > 0) {
        cards.push({
            severity: "med",
            title: "Payables exceeding receivables",
            body: `You owe ${inr(finance.outstanding.payables)} vs ${inr(finance.outstanding.receivables)} incoming. Prioritise collections this week.`,
            cta: { label: "See Finance Hub", path: "/finance-hub" },
        });
    }
    if (finance.cash_basis.revenue_today === 0 && new Date().getHours() >= 14) {
        cards.push({
            severity: "med",
            title: "No sales recorded yet today",
            body: "Half the day is gone — check with the shop floor and record any pending invoices.",
            cta: { label: "Open Sales Hub", path: "/sales-hub" },
        });
    }
    if (cards.length === 0) {
        // Positive frame
        const top = exec.opportunities.top_products[0];
        cards.push({
            severity: "positive",
            title: "Business looking healthy",
            body: top
                ? `${top.name} is the top performer this period (${inr(top.revenue)}). Consider promoting a bundle around it.`
                : "All key metrics are green. Explore Business Intelligence to spot new opportunities.",
            cta: { label: top ? "See Business Intelligence" : "Open BI", path: "/business-intelligence" },
        });
    }
    return cards[0];
}

// --------- Morning Brief ---------
function MorningBrief({ data, loading, error, onOpenBI }) {
    if (loading) return <MorningBriefSkeleton />;
    if (error) return (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800" data-testid="morning-brief-error">
            Couldn&apos;t load morning brief: {error}
        </div>
    );
    if (!data) return null;

    const { finance, financeYesterday, exec, recommendation, health } = data;
    const revToday = finance.cash_basis.revenue_today;
    const revYesterday = financeYesterday?.cash_basis?.revenue_today ?? 0;
    const change = revYesterday > 0 ? Math.round(((revToday - revYesterday) / revYesterday) * 100) : (revToday > 0 ? 100 : 0);
    const topProduct = exec.opportunities.top_products[0];

    const cashPct = revToday > 0 ? Math.round((finance.cash_basis.by_method.cash / revToday) * 100) : 0;
    const upiPct = revToday > 0 ? Math.round((finance.cash_basis.by_method.upi / revToday) * 100) : 0;

    const scoreColor = health.score >= 80 ? "text-emerald-700" : health.score >= 60 ? "text-amber-700" : "text-red-700";
    const scoreRing = health.score >= 80 ? "stroke-emerald-500" : health.score >= 60 ? "stroke-amber-500" : "stroke-red-500";

    return (
        <div className="rounded-xl border border-zinc-200 bg-gradient-to-b from-white to-zinc-50/60 p-5 shadow-sm" data-testid="morning-brief">
            <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
                <div>
                    <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-widest text-violet-700">
                        <Sun className="h-3 w-3" strokeWidth={1.75} /> Morning Brief · {todayISO()}
                    </div>
                    <div className="mt-1 text-lg font-display tracking-tight text-zinc-950">
                        Your business, in one glance.
                    </div>
                </div>
                {health.score !== null && (
                    <div className="flex items-center gap-3" data-testid="brief-health-score">
                        <div className="relative h-14 w-14">
                            <svg viewBox="0 0 40 40" className="h-14 w-14 -rotate-90">
                                <circle cx="20" cy="20" r="16" strokeWidth="4" className="fill-none stroke-zinc-200" />
                                <circle cx="20" cy="20" r="16" strokeWidth="4" strokeLinecap="round"
                                    className={`fill-none ${scoreRing} transition-all`}
                                    strokeDasharray={`${(health.score / 100) * 100.5} 100.5`} />
                            </svg>
                            <div className={`absolute inset-0 flex items-center justify-center text-sm font-display font-medium ${scoreColor}`}>{health.score}</div>
                        </div>
                        <div>
                            <div className="text-[10px] font-medium uppercase tracking-widest text-zinc-500">Business Health</div>
                            <div className="text-xs text-zinc-600 mt-0.5 max-w-[280px]">
                                {health.score >= 80 ? "Excellent — everything looking green" : health.score >= 60 ? "Steady — a few things to watch" : "Needs attention — see recommendations below"}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* KPI strip */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                <BriefKpi
                    testId="brief-revenue"
                    label="Revenue Today"
                    value={inr(revToday)}
                    change={change}
                    changeLabel="vs yesterday"
                />
                <BriefKpi
                    testId="brief-cash-upi"
                    label="Cash / UPI split"
                    value={revToday > 0 ? `${cashPct}% / ${upiPct}%` : "—"}
                    hint={revToday > 0 ? `${inr(finance.cash_basis.by_method.cash)} · ${inr(finance.cash_basis.by_method.upi)}` : "No payments yet"}
                    icon={Coins}
                />
                <BriefKpi
                    testId="brief-receivables"
                    label="Receivables"
                    value={inr(finance.outstanding.receivables)}
                    tone={finance.outstanding.receivables > 0 ? "warn" : "good"}
                />
                <BriefKpi
                    testId="brief-payables"
                    label="Payables"
                    value={inr(finance.outstanding.payables)}
                    tone={finance.outstanding.payables > 0 ? "warn" : "good"}
                />
                <BriefKpi
                    testId="brief-low-stock"
                    label="Low Stock"
                    value={exec.attention.low_stock_skus}
                    hint={exec.attention.out_of_stock_skus > 0 ? `${exec.attention.out_of_stock_skus} out of stock` : "SKUs need reorder"}
                    tone={exec.attention.low_stock_skus > 0 ? "warn" : "good"}
                    icon={AlertTriangle}
                />
                <BriefKpi
                    testId="brief-top-product"
                    label="Top Seller"
                    value={topProduct ? topProduct.name : "—"}
                    hint={topProduct ? `${inr(topProduct.revenue)} this period` : "No sales in period"}
                    icon={Trophy}
                    compact
                />
            </div>

            {/* AI Recommendation */}
            {recommendation && (
                <div className={`mt-5 rounded-lg border p-4 ${
                    recommendation.severity === "high" ? "border-red-200 bg-red-50/40"
                    : recommendation.severity === "med" ? "border-amber-200 bg-amber-50/40"
                    : "border-emerald-200 bg-emerald-50/40"
                }`} data-testid="brief-ai-recommendation">
                    <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                            <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-widest text-violet-700">
                                <Sparkles className="h-3 w-3" strokeWidth={1.75} /> AI Recommendation of the day
                            </div>
                            <div className="mt-2 text-sm font-medium text-zinc-950">{recommendation.title}</div>
                            <div className="mt-1 text-sm text-zinc-700">{recommendation.body}</div>
                        </div>
                        {recommendation.cta && (
                            <Button size="sm" variant="outline" onClick={() => onOpenBI(recommendation.cta.path)} className="h-8 flex-shrink-0" data-testid="brief-recommendation-cta">
                                {recommendation.cta.label} <ArrowRight className="ml-1 h-3 w-3" />
                            </Button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function BriefKpi({ label, value, hint, change, changeLabel, icon: Icon, tone = "default", testId, compact = false }) {
    const toneMap = { default: "text-zinc-950", good: "text-emerald-700", warn: "text-amber-700", bad: "text-red-700" };
    return (
        <div className="rounded-md border border-zinc-200 bg-white p-3" data-testid={testId}>
            <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-widest text-zinc-500">
                {Icon && <Icon className="h-3 w-3" strokeWidth={1.5} />} {label}
            </div>
            <div className={`mt-1.5 font-display tracking-tight ${toneMap[tone]} ${compact ? "text-sm line-clamp-1" : "text-lg"}`}>{value}</div>
            {change !== undefined && change !== null && (
                <div className={`mt-0.5 flex items-center gap-0.5 text-[11px] ${change >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                    {change >= 0 ? <TrendingUp className="h-2.5 w-2.5" /> : <TrendingDown className="h-2.5 w-2.5" />}
                    {change > 0 ? "+" : ""}{change}% {changeLabel && <span className="text-zinc-500 ml-1">{changeLabel}</span>}
                </div>
            )}
            {hint && change === undefined && <div className="mt-0.5 text-[11px] text-zinc-500 truncate">{hint}</div>}
        </div>
    );
}

function MorningBriefSkeleton() {
    return (
        <div className="rounded-xl border border-zinc-200 bg-white p-5 space-y-4" data-testid="morning-brief-loading">
            <div className="flex justify-between">
                <div className="space-y-2"><div className="h-3 w-32 bg-zinc-100 rounded animate-pulse" /><div className="h-5 w-48 bg-zinc-100 rounded animate-pulse" /></div>
                <div className="h-14 w-14 rounded-full bg-zinc-100 animate-pulse" />
            </div>
            <div className="grid grid-cols-6 gap-3">
                {[0,1,2,3,4,5].map((i) => <div key={i} className="h-20 bg-zinc-100 rounded animate-pulse" />)}
            </div>
        </div>
    );
}

// --------- Main Dashboard ---------
export default function Dashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const first = (user?.name || "there").split(" ")[0];

    const [brief, setBrief] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const [finance, financeYesterday, exec] = await Promise.all([
                    api.get("/finance/dashboard"),
                    api.get(`/finance/dashboard?date=${yesterdayISO()}`),
                    api.get("/bi/executive"),
                ]);
                const health = computeHealthScore({ finance: finance.data, exec: exec.data });
                const recommendation = computeRecommendation({ finance: finance.data, exec: exec.data });
                setBrief({
                    finance: finance.data,
                    financeYesterday: financeYesterday.data,
                    exec: exec.data,
                    health,
                    recommendation,
                });
            } catch (err) {
                setError(formatApiError(err));
                toast.error(formatApiError(err));
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    const finance = brief?.finance;

    return (
        <div data-testid="page-dashboard">
            <PageHeader
                title={`Good to see you, ${first}.`}
                description="Your business at a glance — powered by live Finance Hub and Business Intelligence data."
                actions={
                    <Button size="sm" variant="outline" onClick={() => navigate("/app/business-intelligence")} data-testid="dashboard-open-bi">
                        <BarChart3 className="mr-1.5 h-3.5 w-3.5" strokeWidth={1.5} /> Deep dive
                    </Button>
                }
            />

            <div className="p-8 space-y-6 max-w-[1400px]">
                <MorningBrief data={brief} loading={loading} error={error} onOpenBI={(path) => navigate(path)} />

                {/* Secondary row — health breakdown + quick actions + cashflow */}
                <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                    <WidgetCard title="Health breakdown" subtitle="What's contributing to the score" testId="widget-health-breakdown" className="lg:col-span-2">
                        {loading ? (
                            <div className="space-y-2 py-3">
                                {[0,1,2,3].map((i) => <div key={i} className="h-6 bg-zinc-100 rounded animate-pulse" />)}
                            </div>
                        ) : brief?.health?.breakdown?.length ? (
                            <div className="space-y-1.5">
                                {brief.health.breakdown.map((b, i) => (
                                    <div key={i} className="flex items-center justify-between py-1.5 border-b border-zinc-100 last:border-0" data-testid={`health-item-${i}`}>
                                        <div className="flex items-center gap-2">
                                            <div className={`h-1.5 w-1.5 rounded-full ${b.good ? "bg-emerald-500" : "bg-zinc-300"}`} />
                                            <span className="text-sm text-zinc-800">{b.label}</span>
                                        </div>
                                        <span className={`text-xs font-mono ${b.delta > 0 ? "text-emerald-700" : "text-zinc-400"}`}>
                                            {b.delta > 0 ? `+${b.delta}` : "—"}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="py-6 text-center text-sm text-zinc-500">Health signals will appear as data flows in.</div>
                        )}
                    </WidgetCard>

                    <WidgetCard title="Quick actions" testId="widget-quick-actions">
                        <div className="grid grid-cols-1 gap-2">
                            <QuickActionButton icon={Package} label="Add Product" testId="qa-product" onClick={() => navigate("/app/product-brain")} />
                            <QuickActionButton icon={Receipt} label="Record Sale" testId="qa-sale" onClick={() => navigate("/app/sales-hub")} />
                            <QuickActionButton icon={PackagePlus} label="Receive Stock" testId="qa-receive" onClick={() => navigate("/app/warehouse-hub")} />
                            <QuickActionButton icon={ShoppingCart} label="Create Purchase Order" testId="qa-po" onClick={() => navigate("/app/procurement")} />
                            <QuickActionButton icon={Wallet} label="Record Expense" testId="qa-expense" onClick={() => navigate("/app/finance-hub?action=record-expense")} />
                            <QuickActionButton icon={Users} label="Add Customer" testId="qa-customer" onClick={() => navigate("/app/customer-brain")} />
                            <QuickActionButton icon={Truck} label="Add Supplier" testId="qa-supplier" onClick={() => navigate("/app/supplier-brain")} />
                            <QuickActionButton icon={BarChart3} label="Business Intelligence" testId="qa-bi" onClick={() => navigate("/app/business-intelligence")} />
                        </div>
                    </WidgetCard>
                </section>

                {/* 30-day cash flow strip */}
                {finance && (
                    <WidgetCard title="Cash flow · Last 30 days" subtitle="Daily net (inflow − outflow)" testId="widget-cashflow"
                        action={
                            <button onClick={() => navigate("/app/finance-hub")} className="text-[11px] text-violet-700 hover:underline flex items-center gap-1">
                                Open Finance Hub <ArrowRight className="h-3 w-3" />
                            </button>
                        }
                    >
                        <MiniCashflow series={finance.cash_flow_30d || []} />
                        <div className="grid grid-cols-3 gap-3 mt-3 pt-3 border-t border-zinc-100">
                            <div><div className="text-[10px] uppercase tracking-widest text-emerald-700">Inflow (30d)</div><div className="font-mono text-sm mt-0.5">{inr(finance.cash_flow_30d.reduce((s, x) => s + x.inflow, 0))}</div></div>
                            <div><div className="text-[10px] uppercase tracking-widest text-red-700">Outflow (30d)</div><div className="font-mono text-sm mt-0.5">{inr(finance.cash_flow_30d.reduce((s, x) => s + x.outflow, 0))}</div></div>
                            <div><div className="text-[10px] uppercase tracking-widest text-zinc-700">Net</div><div className="font-mono text-sm mt-0.5">{inr(finance.cash_flow_30d.reduce((s, x) => s + x.net, 0))}</div></div>
                        </div>
                    </WidgetCard>
                )}
            </div>
        </div>
    );
}

function MiniCashflow({ series }) {
    if (!series.length) return <div className="py-6 text-sm text-zinc-500">No cash flow yet.</div>;
    const w = 800, h = 80, pad = 4;
    const nets = series.map((s) => s.net);
    const min = Math.min(0, ...nets); const max = Math.max(0, ...nets);
    const range = Math.max(max - min, 1);
    const step = (w - pad * 2) / (series.length - 1 || 1);
    const y = (v) => h - pad - ((v - min) / range) * (h - pad * 2);
    const path = series.map((s, i) => `${i === 0 ? "M" : "L"} ${pad + i * step} ${y(s.net)}`).join(" ");
    return (
        <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-20" data-testid="dashboard-cashflow-svg">
            <line x1={pad} y1={y(0)} x2={w - pad} y2={y(0)} stroke="#e4e4e7" strokeDasharray="2 3" />
            <path d={path} fill="none" stroke="#18181b" strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
    );
}
